import { useState, useEffect } from 'react';
import api from '@/api';
import { RefreshCw, CheckCircle2, XCircle, Clock, Plus, Trash2, Loader2, Copy, ExternalLink, FileSpreadsheet, ChevronDown, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const INTERVAL_OPTS = [
  { value: 5, label: 'Every 5 minutes' },
  { value: 15, label: 'Every 15 minutes' },
  { value: 30, label: 'Every 30 minutes' },
  { value: 60, label: 'Every hour' },
];

function SyncStatusBadge({ status }) {
  if (!status) return <span className="text-[#5C6A7A] font-['JetBrains_Mono'] text-xs">Never synced</span>;
  return status === 'SUCCESS'
    ? <span className="flex items-center gap-1 text-[#57EB31] text-xs font-['JetBrains_Mono']"><CheckCircle2 size={11} /> SUCCESS</span>
    : <span className="flex items-center gap-1 text-red-400 text-xs font-['JetBrains_Mono']"><XCircle size={11} /> FAILED</span>;
}

export default function SheetsSync() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState({});
  const [showAdd, setShowAdd] = useState(false);
  const [showTemplate, setShowTemplate] = useState(false);
  const [templateInfo, setTemplateInfo] = useState(null);
  const [showScript, setShowScript] = useState(false);
  const [form, setForm] = useState({ sheet_id: '', sheet_name: 'Pricing', enabled: true, interval_minutes: 15 });
  const [saving, setSaving] = useState(false);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const [statusRes, templateRes] = await Promise.all([
        api.get('/sync/sheets/status'),
        api.get('/sync/sheets/template-info'),
      ]);
      setStatus(statusRes.data);
      setTemplateInfo(templateRes.data);
    } catch { toast.error('Failed to load sync status'); }
    finally { setLoading(false); }
  };

  const triggerSync = async (sheetId) => {
    setSyncing(p => ({ ...p, [sheetId]: true }));
    try {
      const res = await api.post('/sync/sheets/manual', { sheet_id: sheetId });
      toast.success('Sync started! Refreshing in 5 seconds...');
      setTimeout(() => { load(); setSyncing(p => ({ ...p, [sheetId]: false })); }, 5000);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Sync failed');
      setSyncing(p => ({ ...p, [sheetId]: false }));
    }
  };

  const saveConfig = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post('/sync/sheets/configure', form);
      toast.success('Sheet connected!');
      setShowAdd(false);
      setForm({ sheet_id: '', sheet_name: 'Pricing', enabled: true, interval_minutes: 15 });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const removeConfig = async (sheetId) => {
    if (!window.confirm('Remove this Google Sheet connection?')) return;
    await api.delete(`/sync/sheets/configs/${encodeURIComponent(sheetId)}`);
    toast.success('Removed');
    load();
  };

  const copyText = (text) => { navigator.clipboard.writeText(text); toast.success('Copied!'); };

  if (loading) return <div className="flex justify-center py-24"><Loader2 className="text-[#3B82F6] animate-spin" size={28} /></div>;

  const configs = status?.configs || [];
  const logs = status?.recent_logs || [];

  return (
    <div className="p-6 space-y-6 animate-fade-in" data-testid="sheets-sync-page">
      {/* Header */}
      <div className="flex items-center justify-between pb-4 border-b border-[#2D3F5E]/60">
        <div>
          <p className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-[0.15em] text-[#5C6A7A] mb-1">// GOOGLE SHEETS</p>
          <h1 className="text-white font-['Manrope'] font-extrabold text-2xl">Vendor Pricing Sync</h1>
          <p className="text-[#B8C0CC] font-['IBM_Plex_Sans'] text-sm mt-0.5">
            Connect a Google Sheet → portal updates automatically every time the sheet changes
          </p>
        </div>
        <div className="flex gap-3">
          <button onClick={() => setShowTemplate(true)}
            className="flex items-center gap-2 px-3 py-2 border border-[#2D3F5E] text-[#B8C0CC] rounded-sm text-xs hover:border-[#3B82F6]/50 hover:text-[#3B82F6] transition-colors font-['JetBrains_Mono']">
            <FileSpreadsheet size={13} /> Sheet Template
          </button>
          <button onClick={() => setShowAdd(true)} data-testid="add-sheet-btn"
            className="flex items-center gap-2 px-4 py-2 bg-[#3B82F6] text-white rounded-sm font-['Manrope'] font-bold text-sm uppercase tracking-wide hover:bg-[#2563EB] transition-colors">
            <Plus size={14} /> Connect Sheet
          </button>
        </div>
      </div>

      {/* How it works */}
      <div className="grid grid-cols-4 gap-3">
        {[
          { num: '01', title: 'Set up sheet', desc: 'Download template, fill in your vendor prices. One column per vendor, one row per service.' },
          { num: '02', title: 'Share with us', desc: `Share the sheet with: document-ai@millenials-487815.iam.gserviceaccount.com (View access)` },
          { num: '03', title: 'Paste Sheet ID', desc: 'Copy the Sheet ID from the URL (between /d/ and /edit) and paste it here.' },
          { num: '04', title: 'Auto-sync', desc: 'Portal syncs every 15 minutes. Prices update automatically for all vendors in the sheet.' },
        ].map(s => (
          <div key={s.num} className="card-industrial p-4">
            <span className="text-[#3B82F6] font-['JetBrains_Mono'] text-xs font-bold">{s.num}</span>
            <p className="text-white font-['Manrope'] font-bold text-sm mt-1 mb-1">{s.title}</p>
            <p className="text-[#5C6A7A] font-['IBM_Plex_Sans'] text-xs leading-relaxed">{s.desc}</p>
          </div>
        ))}
      </div>

      {/* Connected sheets */}
      <div>
        <p className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest text-[#5C6A7A] mb-3">Connected Sheets</p>
        {configs.length === 0 ? (
          <div className="card-industrial p-8 text-center">
            <FileSpreadsheet size={32} className="mx-auto mb-3 text-[#2D3F5E]" />
            <p className="text-[#5C6A7A] font-['IBM_Plex_Sans'] text-sm mb-3">No Google Sheets connected yet</p>
            <button onClick={() => setShowAdd(true)} className="px-4 py-2 border border-[#2D3F5E] text-[#8B9AAB] rounded-sm text-xs hover:border-[#3B82F6]/50 hover:text-[#3B82F6] transition-colors font-['IBM_Plex_Sans']">
              Connect your first sheet
            </button>
          </div>
        ) : configs.map(cfg => (
          <div key={cfg.sheet_id} className="card-industrial p-5 mb-3">
            <div className="flex items-start justify-between">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 mb-1">
                  <div className={`w-2 h-2 rounded-full flex-shrink-0 ${cfg.enabled ? 'bg-[#57EB31] live-dot' : 'bg-[#5C6A7A]'}`} />
                  <p className="text-white font-['Manrope'] font-bold text-sm">Sheet: {cfg.sheet_name || 'Pricing'}</p>
                  <span className={`text-[9px] font-['JetBrains_Mono'] uppercase px-2 py-0.5 rounded-full ${cfg.enabled ? 'text-[#57EB31] bg-[#57EB31]/10' : 'text-[#5C6A7A] bg-[#1E293B]'}`}>
                    {cfg.enabled ? `Auto-sync every ${cfg.interval_minutes}min` : 'Paused'}
                  </span>
                </div>
                <div className="flex items-center gap-2 mb-2">
                  <code className="text-[10px] font-['JetBrains_Mono'] text-[#3B82F6] bg-[#0A0F1A] px-2 py-0.5 rounded-sm max-w-[300px] truncate">
                    {cfg.sheet_id}
                  </code>
                  <button onClick={() => copyText(cfg.sheet_id)} className="text-[#5C6A7A] hover:text-[#3B82F6]"><Copy size={11} /></button>
                  <a href={`https://docs.google.com/spreadsheets/d/${cfg.sheet_id}`} target="_blank" rel="noopener noreferrer"
                    className="text-[#5C6A7A] hover:text-[#3B82F6]"><ExternalLink size={11} /></a>
                </div>
                <div className="flex items-center gap-4 text-xs font-['IBM_Plex_Sans']">
                  <SyncStatusBadge status={cfg.last_sync_status} />
                  {cfg.last_sync_at && (
                    <span className="text-[#5C6A7A] flex items-center gap-1"><Clock size={10} /> {new Date(cfg.last_sync_at).toLocaleString()}</span>
                  )}
                  {cfg.last_sync_summary && (
                    <span className="text-[#B8C0CC]">
                      {cfg.last_sync_summary.created || 0} created · {cfg.last_sync_summary.updated || 0} updated · {cfg.last_sync_summary.vendors_in_sheet?.length || 0} vendors
                    </span>
                  )}
                </div>
              </div>
              <div className="flex gap-2 flex-shrink-0 ml-4">
                <button onClick={() => triggerSync(cfg.sheet_id)} disabled={syncing[cfg.sheet_id]}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-[#3B82F6] text-white rounded-sm text-xs font-bold font-['Manrope'] uppercase tracking-wide hover:bg-[#2563EB] disabled:opacity-50 transition-colors"
                  data-testid={`sync-now-${cfg.sheet_id?.slice(0,8)}`}>
                  <RefreshCw size={12} className={syncing[cfg.sheet_id] ? 'animate-spin' : ''} />
                  {syncing[cfg.sheet_id] ? 'Syncing...' : 'Sync Now'}
                </button>
                <button onClick={() => removeConfig(cfg.sheet_id)} className="px-2 py-1.5 text-[#2D3F5E] hover:text-red-400 border border-[#2D3F5E] rounded-sm hover:border-red-800/50 transition-colors">
                  <Trash2 size={13} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Sync History */}
      {logs.length > 0 && (
        <div>
          <p className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest text-[#5C6A7A] mb-3">Recent Sync History</p>
          <div className="card-industrial overflow-hidden">
            <table className="w-full text-xs">
              <thead><tr className="border-b border-[#2D3F5E]">
                {['STARTED','STATUS','CREATED','UPDATED','ERRORS','VENDORS','TRIGGERED BY'].map(h => (
                  <th key={h} className="text-left px-4 py-2.5 text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest text-[#5C6A7A]">{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {logs.map((log, i) => (
                  <tr key={log.id} className="border-b border-[#2D3F5E]/30" style={{ background: i%2!==0?'rgba(255,255,255,0.01)':'transparent' }}>
                    <td className="px-4 py-2 font-['JetBrains_Mono'] text-[#5C6A7A]">{log.started_at ? new Date(log.started_at).toLocaleString() : '—'}</td>
                    <td className="px-4 py-2"><SyncStatusBadge status={log.status} /></td>
                    <td className="px-4 py-2 font-['JetBrains_Mono'] text-[#57EB31]">{log.summary?.created ?? '—'}</td>
                    <td className="px-4 py-2 font-['JetBrains_Mono'] text-[#3B82F6]">{log.summary?.updated ?? '—'}</td>
                    <td className="px-4 py-2 font-['JetBrains_Mono'] text-red-400">{log.summary?.errors?.length ?? (log.error ? '!' : 0)}</td>
                    <td className="px-4 py-2 text-[#B8C0CC] font-['IBM_Plex_Sans']">{log.summary?.vendors_in_sheet?.join(', ')?.slice(0, 40) || '—'}</td>
                    <td className="px-4 py-2 text-[#5C6A7A] font-['JetBrains_Mono']">{log.summary?.triggered_by || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Connect Sheet Modal */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="bg-[#1A2332] border-[#2D3F5E] max-w-lg">
          <DialogHeader><DialogTitle className="text-white font-['Manrope']">Connect Google Sheet</DialogTitle></DialogHeader>
          <form onSubmit={saveConfig} className="space-y-4 mt-2">
            <div className="p-3 bg-[#0A0F1A] border border-[#2D3F5E] rounded-sm space-y-1">
              <p className="text-[9px] font-['JetBrains_Mono'] uppercase text-[#5C6A7A]">Share your sheet with this email (View access):</p>
              <div className="flex items-center gap-2">
                <code className="text-[#3B82F6] font-['JetBrains_Mono'] text-xs flex-1">document-ai@millenials-487815.iam.gserviceaccount.com</code>
                <button type="button" onClick={() => copyText('document-ai@millenials-487815.iam.gserviceaccount.com')} className="text-[#5C6A7A] hover:text-[#3B82F6]"><Copy size={13} /></button>
              </div>
            </div>
            <div>
              <label className="block text-[9px] font-['JetBrains_Mono'] uppercase text-[#5C6A7A] mb-1.5">Google Sheet ID *</label>
              <input type="text" required value={form.sheet_id} onChange={e => setForm(p=>({...p,sheet_id:e.target.value.trim()}))}
                placeholder="1BxiMVs0XRA5nFMdKvBdBZjgmUUqptlbs74OgVE2upms"
                className="w-full h-10 px-3 bg-[#0A0F1A] border border-[#2D3F5E] rounded-sm text-white font-['JetBrains_Mono'] text-xs focus:outline-none focus:border-[#3B82F6]"
                data-testid="sheet-id-input" />
              <p className="text-[10px] text-[#5C6A7A] mt-1 font-['IBM_Plex_Sans']">
                Find in URL: docs.google.com/spreadsheets/d/<strong className="text-[#3B82F6]">[SHEET_ID]</strong>/edit
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[9px] font-['JetBrains_Mono'] uppercase text-[#5C6A7A] mb-1.5">Tab Name</label>
                <input type="text" value={form.sheet_name} onChange={e => setForm(p=>({...p,sheet_name:e.target.value}))}
                  className="w-full h-9 px-3 bg-[#0A0F1A] border border-[#2D3F5E] rounded-sm text-white font-['IBM_Plex_Sans'] text-sm focus:outline-none focus:border-[#3B82F6]" />
              </div>
              <div>
                <label className="block text-[9px] font-['JetBrains_Mono'] uppercase text-[#5C6A7A] mb-1.5">Auto-Sync Interval</label>
                <select value={form.interval_minutes} onChange={e => setForm(p=>({...p,interval_minutes:Number(e.target.value)}))}
                  className="w-full h-9 px-3 bg-[#0A0F1A] border border-[#2D3F5E] rounded-sm text-white font-['IBM_Plex_Sans'] text-sm focus:outline-none focus:border-[#3B82F6]">
                  {INTERVAL_OPTS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
            </div>
            <button type="submit" disabled={saving} className="w-full h-10 bg-[#3B82F6] text-white font-['Manrope'] font-bold text-sm uppercase tracking-wide rounded-sm hover:bg-[#2563EB] disabled:opacity-50 flex items-center justify-center gap-2">
              {saving ? <><Loader2 size={14} className="animate-spin" />Connecting...</> : 'Connect & Sync Now'}
            </button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Template Modal */}
      {templateInfo && (
        <Dialog open={showTemplate} onOpenChange={setShowTemplate}>
          <DialogContent className="bg-[#1A2332] border-[#2D3F5E] max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="text-white font-['Manrope']">Google Sheet Template</DialogTitle></DialogHeader>
            <div className="space-y-5 mt-2">
              <p className="text-[#B8C0CC] font-['IBM_Plex_Sans'] text-sm">{templateInfo.description}</p>

              {/* Setup steps */}
              <div className="space-y-2">
                <p className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest text-[#5C6A7A]">Setup Steps</p>
                {templateInfo.setup_steps?.map((step, i) => (
                  <div key={i} className="flex items-start gap-3 text-xs font-['IBM_Plex_Sans']">
                    <span className="text-[#3B82F6] font-['JetBrains_Mono'] flex-shrink-0">{i+1}.</span>
                    <span className="text-[#B8C0CC]">{step.replace(/^\d+\.\s/, '')}</span>
                  </div>
                ))}
              </div>

              {/* Column structure */}
              <div>
                <p className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest text-[#5C6A7A] mb-2">Column Structure</p>
                <div className="overflow-x-auto border border-[#2D3F5E] rounded-sm">
                  <table className="text-xs">
                    <thead><tr className="border-b border-[#2D3F5E] bg-[#0A0F1A]">
                      {['Col','Header','Example','Required','Notes'].map(h => <th key={h} className="text-left px-3 py-2 text-[9px] font-['JetBrains_Mono'] uppercase text-[#5C6A7A] whitespace-nowrap">{h}</th>)}
                    </tr></thead>
                    <tbody>
                      {templateInfo.columns?.map((col, i) => (
                        <tr key={i} className="border-b border-[#2D3F5E]/40">
                          <td className="px-3 py-2 font-['JetBrains_Mono'] text-[#3B82F6]">{col.col}</td>
                          <td className="px-3 py-2 font-bold text-white font-['IBM_Plex_Sans']">{col.header}</td>
                          <td className="px-3 py-2 font-['JetBrains_Mono'] text-[#B8C0CC] text-[11px]">{col.example}</td>
                          <td className="px-3 py-2">{col.required ? <span className="text-[#57EB31] text-[10px]">✓</span> : <span className="text-[#5C6A7A] text-[10px]">opt</span>}</td>
                          <td className="px-3 py-2 text-[#5C6A7A] font-['IBM_Plex_Sans'] text-[11px] max-w-[200px]">{col.note || ''}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Example rows */}
              <div>
                <p className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-widests text-[#5C6A7A] mb-2">Example Rows (first row = header)</p>
                <div className="overflow-x-auto border border-[#2D3F5E] rounded-sm">
                  <table className="text-[10px] font-['JetBrains_Mono']">
                    <thead><tr className="border-b border-[#2D3F5E] bg-[#0A0F1A]">
                      {templateInfo.header_example?.map((h, i) => <th key={i} className="text-left px-2 py-1.5 text-[#3B82F6] whitespace-nowrap">{h}</th>)}
                    </tr></thead>
                    <tbody>
                      {templateInfo.example_rows?.map((row, i) => (
                        <tr key={i} className="border-b border-[#2D3F5E]/30" style={{ background: i%2!==0?'rgba(255,255,255,0.02)':'transparent' }}>
                          {row.map((cell, j) => <td key={j} className="px-2 py-1.5 text-[#B8C0CC] whitespace-nowrap">{cell || <span className="text-[#2D3F5E]">—</span>}</td>)}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Real-time script */}
              <div>
                <button onClick={() => setShowScript(!showScript)} className="flex items-center gap-2 text-xs text-[#3B82F6] hover:underline font-['JetBrains_Mono'] mb-2">
                  {showScript ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                  Optional: Install Apps Script for instant sync (instead of every 15 min)
                </button>
                {showScript && templateInfo.apps_script_for_realtime && (
                  <div className="bg-[#0E0905] border border-[#2D3F5E] rounded-sm p-4">
                    <p className="text-[10px] text-[#5C6A7A] font-['IBM_Plex_Sans'] mb-3">{templateInfo.apps_script_for_realtime.note}</p>
                    <pre className="text-[10px] font-['JetBrains_Mono'] text-[#C9C3BD] whitespace-pre-wrap overflow-x-auto">
                      {templateInfo.apps_script_for_realtime.code}
                    </pre>
                    <button onClick={() => copyText(templateInfo.apps_script_for_realtime.code)}
                      className="mt-2 flex items-center gap-1.5 px-3 py-1.5 border border-[#2D3F5E] text-[#5C6A7A] rounded-sm text-xs hover:border-[#3B82F6]/50 hover:text-[#3B82F6] transition-colors font-['JetBrains_Mono']">
                      <Copy size={11} /> Copy Script
                    </button>
                  </div>
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
